import { CommonUtils } from './../../shared/utils/common-utils';
import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';

import { AppReview } from 'src/providers/app-review/app-review';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { AppState } from 'src/shared/providers/http/app-state';
import { OnRails } from 'src/providers/on-rails/on-rails';

import { SuccessPageParamsModel } from 'src/pages/success/success';
import { CheckLocationPermission } from 'src/behaviors/check-location-permission';
import { TimeoutErrorHandler } from 'src/shared/providers/http/timeout-error-handler';
import { Timeout } from 'src/shared/components/timeout/timeout';
import { ActivatedRoute, Router } from '@angular/router';
import { IdentificationCheck } from 'src/shared/providers/identification-check';
import { Subscription, timer } from 'rxjs'
import { tap } from 'rxjs/operators';
import { Concierge } from 'src/providers/concierge/concierge';
import { HtmlVideoRecorder } from 'src/shared/providers/camera/html-video-recorder';
import { InAppBrowserUtils } from 'src/shared/utils/in-app-browser-utils';
import { IdentityVerificationModalComponent } from 'src/pages/onboarding/identity-verification-modal/identity-verification';
import { ModalController } from '@ionic/angular';
import { MatDialog } from '@angular/material/dialog';
import { Me } from 'src/shared/providers/me';
import * as moment from 'moment';

// "Good partner" reminders shown on the check-in confirm screen. One is picked
// at random each time the screen loads to gently discourage client poaching.
// See Trello #18219. Keep the "Learn more" links in sync with tidy.com/docs.
interface GoodPartnerReminder {
  body: string;
  linkUrl: string;
}

const GOOD_PARTNER_REMINDERS: GoodPartnerReminder[] = [
  {
    body: 'The top way to get booked more is to make sure clients book you back.',
    linkUrl: 'https://tidy.com/docs/pros/scores--reviews',
  },
  {
    body: 'Want to work with a client outside TIDY? Notify us to get clients for just $150 each.',
    linkUrl: 'https://tidy.com/docs/pros/fees-for-clients',
  },
  {
    body: 'Sharing a business card with a client? No problem, it\'s just $150 per client.',
    linkUrl: 'https://tidy.com/docs/pros/fees-for-clients',
  },
];

// Only remind pros during their first stretch on the platform (when poaching is
// most common). Falls open if we can't determine when they went live.
const GOOD_PARTNER_REMINDER_WINDOW_DAYS = 180;

const IDENTIFICATION_CHECK_STATUSES = {
  0: 'requires_input',
  1: 'processing',
  2: 'verified',
}

// Tags a Concierge ticket as an identity-verification report. The backend
// (gotidy CheckedInMoment) treats a same-day ticket with this reason as a
// fallback for the check-in identity gate. Keep in sync with the BE constant.
const VERIFICATION_ISSUE_CONCIERGE_REASON = 'identity_verification_issue';

@Component({
    templateUrl: 'check-in.html',
    styleUrls: ['./check-in.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CheckInPage extends Timeout implements OnDestroy {

  todayCleanings: any;
  errorMessage: any;
  isOnIdentificationCheck: boolean;
  isOnFaceUnlock: boolean;
  identificationSteps: Array<{icon: string, title: string, subtitle: string, displayButton: boolean, buttonText: string, action: () => void}> = [
    {
      icon: 'assets/icon/id.png',
      title: 'These jobs require a quick identification check.',
      subtitle: 'All you need is your drivers license.',
      displayButton: true,
      buttonText: 'Get Verified',
      action: () => this.getVerified(),
    },
    {
      icon: 'assets/icon/identify.png',
      title: 'Identification in progress',
      subtitle: 'This typically takes less than 1 minute.',
      displayButton: false,
      buttonText: '',
      action: () => {},
    },
    {
      icon: 'assets/icon/verify.png',
      title: 'Record a Quick Video',
      subtitle: '',
      displayButton: true,
      buttonText: 'Start Recording',
      action: () => this.startVideoRecording(),
    },
    {
      icon: 'assets/icon/verify.png',
      title: 'Verification Completed!',
      subtitle: 'Your identity has been succesfully verified.',
      displayButton: true,
      buttonText: 'Go to Check In',
      action: () => this.goToCheckIn(),
    }
  ];
  currentIdentificationStep = this.identificationSteps[0];
  verificationUrl = '';
  verificationSubscription = new Subscription();
  identificationError: any;
  biometricAuthVerificationUrl = '';
  portraitUrl = '';
  isPhotoPreviewStep = false;
  lastAttemptStatus = '';
  lastFailureReason = '';
  biometricAuthResponse: any = null;
  isVideoStep = false;
  videoDeadline: moment.Moment;
  lastIdentificationCheck: any;
  goodPartnerReminder: GoodPartnerReminder = null;

  constructor(
    private appReview: AppReview,
    private checkLocationPermission: CheckLocationPermission,
    private customNavCtrl: CustomNavController,
    private onRails: OnRails,
    private appState: AppState,
    private identificationCheck: IdentificationCheck,
    private utils: CommonUtils,
    private concierge: Concierge,
    private htmlVideoRecorder: HtmlVideoRecorder,
    private iabUtils: InAppBrowserUtils,
    private modalCtrl: ModalController,
    private dialog: MatDialog,
    private me: Me,
    timeoutErrorHandler: TimeoutErrorHandler,
    router: Router,
    route: ActivatedRoute
  ) {
    super(timeoutErrorHandler, router, route);
  }

  async ionViewDidEnter() {
    this.loaded = false;
    this.alreadyShowedOfflineOrTimeoutAlert = false;

    if (!await this.appState.isOnline()) {
      this.alreadyShowedOfflineOrTimeoutAlert = true;
      return this.timeoutErrorHandler.showOfflineAlert(this.successPageLoad, 'dashboard', this.retrySubject);
    }
    this.todayCleanings = await this.onRails.getTodayCleaningsToCheckin()
      .catch(err => this.timeoutHandler(err));
    if (!this.todayCleanings || this.todayCleanings.length === 0) {
      this.customNavCtrl.navigateForward('jobs');
      return;
    }
    await this.setGoodPartnerReminder();
    await this.handleIdentificationCheck();

    this.successPageLoad.emit(true);
    this.loaded = true;
  }

  // Picks a random "good partner" reminder to show on the confirm-jobs screen,
  // limited to the pro's first GOOD_PARTNER_REMINDER_WINDOW_DAYS on the platform.
  // See Trello #18219.
  async setGoodPartnerReminder(): Promise<void> {
    this.goodPartnerReminder = null;
    if (!await this.withinGoodPartnerReminderWindow()) {
      return;
    }
    const index = Math.floor(Math.random() * GOOD_PARTNER_REMINDERS.length);
    this.goodPartnerReminder = GOOD_PARTNER_REMINDERS[index];
  }

  // True only when we can confirm the pro went live within the reminder window.
  // Fails closed (hides the reminder) when the go-live date is missing/unparseable
  // so pros past their first 180 days aren't shown it, per the card's targeting.
  private async withinGoodPartnerReminderWindow(): Promise<boolean> {
    try {
      const me = await this.me.load();
      const liveDate = me?.certification?.date;
      if (!liveDate || !moment(liveDate).isValid()) {
        return false;
      }
      return moment().diff(moment(liveDate), 'days') <= GOOD_PARTNER_REMINDER_WINDOW_DAYS;
    } catch (error) {
      console.error('Failed to check good partner reminder window:', error);
      return false;
    }
  }

  openGoodPartnerReminderLink(): void {
    if (this.goodPartnerReminder?.linkUrl) {
      // Shared IAB util: in-app browser on native, new tab on web (and location.href
      // on iOS Safari to dodge the popup blocker).
      this.iabUtils.openUrl(this.goodPartnerReminder.linkUrl);
    }
  }

  ngOnDestroy() {
    this.verificationSubscription?.unsubscribe();
  }

  async handleIdentificationCheck(): Promise<void> {
    const identificationCheck = await this.getIdentificationCheck();
    this.isOnIdentificationCheck = identificationCheck?.status !== IDENTIFICATION_CHECK_STATUSES[2];
    if (this.isOnIdentificationCheck) {
      this.currentIdentificationStep = (this.identificationError || identificationCheck?.status === IDENTIFICATION_CHECK_STATUSES[1]) ? this.identificationSteps[1] : this.identificationSteps[0];
      if (this.currentIdentificationStep === this.identificationSteps[1]) {
        this.startVerificationCheck();
      }
      return;
    }

    // ID is verified - check if video is still needed (only for new verifications)
    if (identificationCheck?.video_required && !identificationCheck?.video_url) {
      this.isOnIdentificationCheck = true;
      this.finishVerificationCheck();
      return;
    }

    await this.handleFaceUnlock();
  }

  async handleFaceUnlock(): Promise<void> {
    const someJobTypeTidy = this.todayCleanings.some((cleaning) => cleaning?.job?.type?.toLowerCase()?.includes('tidy'));
    const someServiceTypeNotPrivate = this.todayCleanings.some((cleaning) => !cleaning?.service_type_details?.is_private);

    const hasAtLeastOneTidyJob = someJobTypeTidy || someServiceTypeNotPrivate;

    if (!hasAtLeastOneTidyJob) {
      this.goToCheckIn();
      return;
    }
    // If the pro already reported a verification problem today, don't force them
    // back through the flaky face-match - send them straight to confirm. The
    // backend honors the same-day report at the identity gate. See card COza39Tq.
    if (this.faceUnlockIssueReportedToday()) {
      this.goToCheckIn();
      return;
    }
    this.isOnFaceUnlock = true;
    this.isPhotoPreviewStep = false;
    this.identificationSteps = [
      {
        icon: 'assets/icon/face-auth.png',
        title: 'Account Security Verification',
        subtitle: 'We need to verify your identity before continuing.',
        displayButton: true,
        buttonText: 'Verify Identity',
        action: () => this.goToPhotoPreview(),
      },
      {
        icon: 'assets/icon/face-auth.png',
        title: 'Review your photo',
        subtitle: 'This is the photo we\'ll compare your face to.',
        displayButton: true,
        buttonText: 'Looks good, continue',
        action: () => this.startBiometricAuth(),
      },
      {
        icon: 'assets/icon/identify.png',
        title: 'Verifying your identity',
        subtitle: 'This typically takes less than 1 minute.',
        displayButton: false,
        buttonText: '',
        action: () => {},
      },
      {
        icon: 'assets/icon/verify.png',
        title: 'Verification Complete',
        subtitle: 'Your account is now secure.',
        displayButton: true,
        buttonText: 'Continue',
        action: () => this.goToCheckIn(),
      }
    ];
    this.currentIdentificationStep = this.identificationSteps[0];
    await this.loadBiometricPortrait();

    // If the biometric check already reads verified (e.g. the BE returns a
    // same-day verified KYC as the biometric result), don't present a redundant
    // verification gate - go straight to the completed step. See Issue 2.
    if (this.biometricAuthResponse?.status === 'verified') {
      this.finishVerificationCheck();
    }
  }

  // Fetches the comparison portrait + last failure reason so the pro can review the
  // photo their face will be matched against before starting biometric auth. Does NOT
  // open the verification URL - that only happens once the pro confirms the photo.
  async loadBiometricPortrait(): Promise<void> {
    try {
      const biometricAuth = await this.identificationCheck.getBiometricAuth();
      // Cache the full response. The backend only returns a usable redirect_url
      // on the FIRST call per session, so startBiometricAuth must reuse this one
      // instead of fetching again (a second fetch returns redirect_url: null).
      this.biometricAuthResponse = biometricAuth;
      this.portraitUrl = this.usableUrl(biometricAuth?.portrait_url);
      this.lastFailureReason = biometricAuth?.last_failure_reason || '';
      this.lastAttemptStatus = biometricAuth?.last_attempt_status || '';
    } catch (error) {
      console.error('Failed to load biometric portrait:', error);
    }
  }

  // The status chip text, hidden for uninformative statuses like "Not Started".
  get attemptStatusLabel(): string {
    const normalized = (this.lastAttemptStatus || '').toLowerCase().replace(/[\s_]/g, '');
    if (!normalized || normalized === 'notstarted') {
      return '';
    }
    return this.lastAttemptStatus;
  }

  // Friendly headline for a failed previous biometric attempt, keyed off the
  // raw Didit status. The specific reason is shown verbatim below it.
  get attemptFailureTitle(): string {
    switch ((this.lastAttemptStatus || '').toLowerCase()) {
      case 'expired':
        return 'Your verification link expired';
      case 'abandoned':
        return "Your last verification wasn't finished";
      default:
        return "We couldn't verify your identity last time";
    }
  }

  goToPhotoPreview(): void {
    // No comparison photo available (legacy/no portrait) - behave as today.
    if (!this.portraitUrl) {
      this.startBiometricAuth();
      return;
    }
    this.isPhotoPreviewStep = true;
    this.currentIdentificationStep = this.identificationSteps[1];
  }

  reportPortraitIssue(): void {
    this.reportVerificationIssue({
      type: 'support.homekeeper_other',
      data: {
        subject: 'Incorrect verification photo',
        body: 'The photo shown for my account security verification does not look like me. Please review my verification photo.',
        emails: [],
        attachments: [],
      },
      metadata: { reason: VERIFICATION_ISSUE_CONCIERGE_REASON },
    });
  }

  // The API can serialize an empty URL field as {} or null. Only treat a
  // non-empty string as a usable URL (used for redirect_url and portrait_url).
  private usableUrl(value: any): string {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : '';
  }

  async startBiometricAuth(): Promise<void> {
    this.isPhotoPreviewStep = false;
    try {
      // Reuse the response captured on page load (it holds the one-time
      // redirect_url); only fetch if we don't have it yet (e.g. load failed).
      const biometricAuth = this.biometricAuthResponse
        || await this.identificationCheck.getBiometricAuth();
      // It has been consumed - the next attempt must re-fetch a fresh session.
      this.biometricAuthResponse = null;
      const redirectUrl = this.usableUrl(biometricAuth?.redirect_url);
      if (biometricAuth?.status === 'verified') {
        this.finishVerificationCheck();
      } else if (redirectUrl) {
        this.biometricAuthVerificationUrl = redirectUrl;
        this.iabUtils.openUrl(redirectUrl);
        this.currentIdentificationStep = this.identificationSteps[2];
        this.startBiometricAuthPolling();
      } else if (biometricAuth?.status === 'processing') {
        // A decision is already in progress and there's no URL to open - show
        // the "Verifying" step and poll until Didit resolves it.
        this.currentIdentificationStep = this.identificationSteps[2];
        this.startBiometricAuthPolling();
      } else {
        this.utils.showError('Unable to start verification. Please try again.', 5000, false);
      }
    } catch (error) {
      console.error('Failed to start biometric auth:', error);
      const message = error?.error?.message || error?.message || 'Unable to start verification. Please try again.';
      this.utils.showError(message, 5000, false);

      if (message.toLowerCase().includes('portrait')) {
        this.openIdentityVerificationModal();
      }
    }
  }

  async startBiometricAuthPolling(): Promise<void> {
    const fifteenSeconds = 15000;
    this.verificationSubscription.add(timer(fifteenSeconds, fifteenSeconds)
      .pipe(
        tap(async () => {
          try {
            const biometricAuth = await this.identificationCheck.getBiometricAuth();
            if (biometricAuth?.status === 'verified') {
              this.finishVerificationCheck();
              this.verificationSubscription.unsubscribe();
            }
          } catch (error) {
            console.error('Biometric auth polling error:', error);
          }
        })
      )
      .subscribe());
  }

  async openIdentityVerificationModal(): Promise<void> {
    const isMobileResolution = window.innerWidth <= 870;
    if (!isMobileResolution) {
      const dialog = this.dialog.open(IdentityVerificationModalComponent, {
        width: '500px',
      });
      dialog.afterClosed().subscribe(() => {
        this.handleIdentityVerificationModalClose();
      });
    } else {
      const modal = await this.modalCtrl.create({
        component: IdentityVerificationModalComponent,
        mode: 'ios',
        backdropDismiss: true,
        showBackdrop: true,
        breakpoints: [0, 0.4, 0.5, 0.8, 1],
        initialBreakpoint: 1,
        presentingElement: await this.modalCtrl.getTop(),
        canDismiss: true,
      });
      await modal.present();
      modal.onDidDismiss().then(() => {
        this.handleIdentityVerificationModalClose();
      });
    }
  }

  handleIdentityVerificationModalClose(): void {
    // Re-trigger the face unlock flow so biometric auth can retry with the new portrait
    this.handleFaceUnlock();
  }

  // Labeled progress for the verification flows so the pro can see where they
  // are and what's next. Derived (read-only) from the current step + flow, so
  // it stays correct without any extra bookkeeping at transition points.
  get verificationStepper(): Array<{ label: string; state: 'done' | 'active' | 'upcoming' }> {
    const idx = this.identificationSteps.indexOf(this.currentIdentificationStep);
    let labels: string[];
    let activeStage: number;

    if (this.isOnFaceUnlock) {
      // steps: 0 security, 1 photo preview, 2 verifying, 3 complete.
      // The photo-preview step only happens when a stored portrait exists, so
      // only surface the "Review photo" stage in that case (otherwise the pro
      // would see a stage that gets skipped).
      if (this.portraitUrl) {
        labels = ['Review photo', 'Verify', 'Done'];
        activeStage = idx <= 1 ? 0 : (idx === 2 ? 1 : 2);
      } else {
        labels = ['Verify', 'Done'];
        activeStage = idx === 3 ? 1 : 0;
      }
    } else if (this.lastIdentificationCheck?.video_required) {
      // steps: 0 intro, 1 in-progress, 2 video, 3 complete (idx -1 = uploading)
      labels = ['Verify ID', 'Record video', 'Done'];
      if (idx === 3) activeStage = 2;
      else if (idx === 2 || idx === -1 || this.isVideoStep) activeStage = 1;
      else activeStage = 0;
    } else {
      labels = ['Verify ID', 'Done'];
      activeStage = idx === 3 ? 1 : 0;
    }

    const lastStage = labels.length - 1;
    return labels.map((label, i) => {
      let state: 'done' | 'active' | 'upcoming';
      if (i < activeStage) state = 'done';
      else if (i > activeStage) state = 'upcoming';
      else state = activeStage === lastStage ? 'done' : 'active';
      return { label, state };
    });
  }

  trackStepByLabel(_index: number, step: { label: string }): string {
    return step.label;
  }

  goToVerificationPage(): void {
    this.iabUtils.openUrl(this.verificationUrl);
  }

  getVerified(): void {
    this.startVerificationCheck();
    this.goToVerificationPage();
    this.currentIdentificationStep = this.identificationSteps[1];
  }

  async startVerificationCheck(): Promise<void> {
    const fifteenSeconds = 15000;
    this.verificationSubscription.add(timer(0, fifteenSeconds)
      .pipe(
        tap(async () => {
          const identificationCheck = await this.getIdentificationCheck()
          if (identificationCheck.status === IDENTIFICATION_CHECK_STATUSES[2]) {
            this.finishVerificationCheck();
            this.verificationSubscription.unsubscribe();
          }
        })
      )
      .subscribe());
  }

  finishVerificationCheck(): void {
    // Verification is done (Didit/KYC/biometric resolved). Close the in-app
    // browser so the pro is returned to the app instead of being left on the
    // Didit redirect/website page having to close it manually (Issue 47).
    // Safe no-op when no browser is open (already-verified paths).
    void this.iabUtils.closeUrl();

    // If in face unlock flow, just show the final step (no video needed)
    if (this.isOnFaceUnlock) {
      this.isPhotoPreviewStep = false;
      this.identificationError = null;
      this.lastFailureReason = '';
      this.lastAttemptStatus = '';
      // Face unlock steps array has 4 elements (0-3): security, photo preview,
      // verifying, complete. The final "Verification Complete" step is index 3.
      this.currentIdentificationStep = this.identificationSteps[3];
      return;
    }

    const check = this.lastIdentificationCheck;

    // If video not required (legacy user) or already uploaded, show success
    if (!check?.video_required || check?.video_url) {
      this.showFinalStep();
      return;
    }

    // Video required but not uploaded - show video step
    this.currentIdentificationStep = this.identificationSteps[2];
    this.isVideoStep = true;

    // Set deadline (show max 5 min even if more time, default to 5 min if not provided)
    const fiveMinFromNow = moment().add(5, 'minutes');
    if (check?.video_upload_deadline) {
      const deadline = moment(check.video_upload_deadline);
      this.videoDeadline = deadline.isBefore(fiveMinFromNow) ? deadline : fiveMinFromNow;
    } else {
      this.videoDeadline = fiveMinFromNow;
    }
  }

  async startVideoRecording(): Promise<void> {
    this.htmlVideoRecorder.openVideoRecorderModal().then(async (videoBlob) => {
      if (videoBlob) {
        this.uploadVideo(videoBlob).then(() => {});
      }
    }).catch((error) => {
      this.utils.showError('Video recording failed. Please try again.', 5000, false);
    });
  }

  async uploadVideo(videoBlob: Blob): Promise<void> {
    // Hide video instructions during upload
    this.isVideoStep = false;

    // Show uploading state
    this.currentIdentificationStep = {
      icon: 'assets/icon/identify.png',
      title: 'Uploading video...',
      subtitle: 'Please wait.',
      displayButton: false,
      buttonText: '',
      action: () => {},
    };

    try {
      const s3Response = await this.identificationCheck.uploadVideo(videoBlob);
      await this.identificationCheck.submitVideoUrl(s3Response.Location);
      this.showFinalStep();
    } catch (error) {
      this.utils.showError('Video upload failed. Please try again.', 5000, false);
      // Let user retry - show video step again with error
      this.currentIdentificationStep = this.identificationSteps[2];
      this.isVideoStep = true;
      const responseError = error?.error?.message || error?.message;
      const windowExpired = 'window has expired';
      if (responseError && responseError.includes(windowExpired)) {
        this.identificationError = { reason: `Video upload window has expired. Please restart the identification check.`}
        this.currentIdentificationStep = this.identificationSteps[0];
        return;
      }
      this.identificationError = { reason: `Video upload failed. Please try again. ${responseError}`}
    }
  }

  showFinalStep(): void {
    this.isVideoStep = false;
    this.identificationError = null;
    // Both flows now use index 3 as the final step:
    // - face unlock: 4 elements (security, photo preview, verifying, complete)
    // - normal identification: 4 elements (intro, in-progress, video, complete)
    const finalStepIndex = 3;
    this.currentIdentificationStep = this.identificationSteps[finalStepIndex];
  }

  async getIdentificationCheck(): Promise<any> {
    const identificationCheck = await this.identificationCheck.getIdentityCheck();
    this.lastIdentificationCheck = identificationCheck;
    this.verificationUrl = identificationCheck?.redirect_url;
    this.identificationError = this.usableError(identificationCheck?.last_error_message);
    console.log(identificationCheck);
    return identificationCheck;
  }

  // The FE response mapper can turn a null last_error_message into {}. Treat an
  // empty object (or blank/absent value) as "no error" so it doesn't read as a
  // truthy error and push the pro to the "verifying" step. A real error is a
  // non-empty Hash (e.g. { reason: ... }). See Issue 3.
  private usableError(value: any): any {
    if (value == null) {
      return null;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0 ? value : null;
    }
    if (typeof value === 'object') {
      return Object.keys(value).length > 0 ? value : null;
    }
    return value;
  }

  goToCheckIn(): void {
    this.isOnIdentificationCheck = false;
    this.isOnFaceUnlock = false;
  }

  async confirm() {
    const locationCheck = await this.checkLocationPermission.requestLocationPermission();

    try {
      await this.onRails.confirmCleanings(this.extractJobIds());
      const params = this.successParams(locationCheck.success);
      this.customNavCtrl.navigateForward('success', params);
    } catch (err) {
      if (!await this.appState.isOnline()) {
        this.errorMessage = 'You Must Be Online To Check In';
        this.utils.showError(this.errorMessage);
      } else {
        const message = err.message || err.error?.message || '';
        if (message.toLowerCase().includes('identity verification')) {
          // Route an identity-gate failure back through verification rather than
          // silently landing on the dashboard with jobs unconfirmed - that path
          // was a silent no-show + fee, the exact harm COza39Tq set out to avoid.
          // The bypass cap is now enforced up front (a blocked pro never reaches
          // confirm-jobs), so there's no reported-today case to special-case here.
          this.errorMessage = '';
          await this.handleIdentificationCheck();
        } else {
          // Never surface a blank/opaque error — the Pro must get a clear signal
          // that check-in did not go through so they can retry. See Trello #18064.
          this.errorMessage = message || 'We could not confirm your check-in. Please try again.';
          this.utils.showError(this.errorMessage);
        }
      }
    }
  }

  extractJobIds(): number[] {
    return this.todayCleanings.map( item => item.job.id );
  }

  callOut = () => {
    this.customNavCtrl.navigateForward('check-in-learn-about-teams', {cleanings: this.todayCleanings});
  }

  successParams(successLocation: boolean): SuccessPageParamsModel {
    return {
      header: 'Checked in for Work',
      body: 'Remember: Clients usually book based on your retention score.\
      The best thing you can do to get more Clients is to keep existing Clients.<br><br>\
      Clients tell us the most important reasons they will book with you again are:<br><br>\
      1) Keep your appointments<br>\
      2) Work hard the full job time<br>\
      3) Maintain a positive Attitude',
      buttonText: 'Ok',
      buttonRoute: successLocation ? 'dashboard' : 'location-warning'
    };
  }

  reportFaceUnlockIssues(): void {
    this.reportVerificationIssue({
      type: 'support.homekeeper_other',
      data: {
        subject: 'Account Security Verification Issues',
        body: 'I am having trouble completing the account security verification.',
        emails: [],
        attachments: [],
      },
      metadata: { reason: VERIFICATION_ISSUE_CONCIERGE_REASON },
    });
  }

  // A pro reporting a verification problem must still confirm their jobs first,
  // or they're left unconfirmed and auto-no-showed + fee'd (card COza39Tq). We
  // file the (tagged) support ticket up front - the backend uses a same-day
  // tagged ticket as a fallback for the identity gate so confirm-jobs succeeds -
  // then route them to the confirm-jobs screen to mark they'll be there.
  private async reportVerificationIssue(payload: any): Promise<void> {
    const loading = await this.utils.showLoading();
    try {
      const conciergeItem = await this.concierge.addConciergeItem(payload);
      loading.dismiss();

      // The backend caps this bypass to once per 30 days. When it isn't allowed,
      // keep the pro on the face-match instead of skipping it - otherwise a pro
      // could dodge the daily biometric indefinitely (card EecscV5N). Only an
      // explicit `false` blocks; a missing field (older backend) still skips, so
      // this degrades safely.
      if (conciergeItem?.identity_issue_bypass_allowed === false) {
        this.utils.showError(
          "You've already skipped the security selfie in the last 30 days. Please complete it now to check in.",
          6000,
        );
        return;
      }

      localStorage.setItem('faceUnlockIssueReportedDate', new Date().toLocaleDateString('en-CA'));
      this.goToCheckIn();
      this.utils.showSuccess("Report sent. Confirm you'll be at your jobs below so you aren't marked absent.");
    } catch (err) {
      console.error(err);
      loading.dismiss();
      this.utils.showError('There was an error reporting your issue. Please try again later.', 5000);
    }
  }

  private faceUnlockIssueReportedToday(): boolean {
    return localStorage.getItem('faceUnlockIssueReportedDate') === new Date().toLocaleDateString('en-CA');
  }
}
